import React, { useState } from 'react';
import { Row, Col, Card, Badge, Modal, Button, Spinner } from 'react-bootstrap';

const DocumentGrid = ({ documents = [] }) => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [imageErrors, setImageErrors] = useState({});

  // Debug logging
  React.useEffect(() => {
    if (documents && documents.length > 0) {
      console.log('📋 DocumentGrid received documents:', documents.length);
      documents.forEach((doc, index) => {
        console.log(`  ${index + 1}. ${doc.documentName}:`);
        console.log(`     - ID: ${doc.id}`);
        console.log(`     - Image URL exists: ${!!doc.imageUrl}`);
        console.log(
          `     - Image URL preview: ${doc.imageUrl ? doc.imageUrl.substring(0, 100) + '...' : 'None'}`,
        );
      });
    }
  }, [documents]);

  const handleDocumentClick = (doc) => {
    setSelectedDocument(doc);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDocument(null);
  };

  const handleImageLoad = (docId) => {
    setImageLoadingStates((prev) => ({ ...prev, [docId]: false }));
  };

  const handleImageError = (docId) => {
    console.error(`Failed to load image for document ${docId}`);
    setImageLoadingStates((prev) => ({ ...prev, [docId]: false }));
    setImageErrors((prev) => ({ ...prev, [docId]: true }));
  };

  const handleImageLoadStart = (docId) => {
    setImageLoadingStates((prev) => ({ ...prev, [docId]: true }));
  };

  if (!documents || documents.length === 0) {
    return null;
  }

  // Smaller thumbnails to fit 4-5 across
  const colSize = 3; // This gives us 4 columns on desktop
  const mdColSize = 3; // 4 columns on medium screens
  const smColSize = 4; // 3 columns on small screens

  return (
    <>
      <div className="document-grid-container mt-3">
        <div className="document-grid-header mb-2">
          <small className="text-muted">
            📄 {documents.length} relevant document
            {documents.length !== 1 ? 's' : ''} found
          </small>
        </div>

        <Row className="g-2">
          {documents.map((doc) => (
            <Col key={doc.id} xs={6} sm={smColSize} md={mdColSize} lg={colSize}>
              <Card
                className="document-card h-100 shadow-sm"
                onClick={() => handleDocumentClick(doc)}
                style={{ cursor: 'pointer' }}
              >
                <div
                  style={{
                    position: 'relative',
                    paddingTop: '129.7%',
                    overflow: 'hidden',
                  }}
                >
                  {imageLoadingStates[doc.id] && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8f9fa',
                      }}
                    >
                      <Spinner animation="border" size="sm" />
                    </div>
                  )}
                  {imageErrors[doc.id] ? (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d',
                        fontSize: '12px',
                        textAlign: 'center',
                        padding: '8px',
                      }}
                    >
                      📄
                      <br />
                      Image unavailable
                    </div>
                  ) : (
                    <img
                      src={doc.imageUrl}
                      alt={doc.documentName}
                      onLoadStart={() => handleImageLoadStart(doc.id)}
                      onLoad={() => handleImageLoad(doc.id)}
                      onError={() => handleImageError(doc.id)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: imageLoadingStates[doc.id] ? 'none' : 'block',
                      }}
                    />
                  )}
                  <Badge
                    bg="dark"
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      fontSize: '0.75rem',
                    }}
                  >
                    Page {doc.pageNumber}
                    {doc.totalPages && ` of ${doc.totalPages}`}
                  </Badge>
                </div>
                <Card.Body className="p-1">
                  <Card.Title
                    style={{
                      fontSize: '0.75rem',
                      lineHeight: '1.2',
                      marginBottom: '0.25rem',
                    }}
                  >
                    {doc.documentName}
                  </Card.Title>
                  <Card.Text
                    style={{
                      fontSize: '0.65rem',
                      color: '#666',
                      marginBottom: '0.25rem',
                    }}
                  >
                    {doc.summary}
                  </Card.Text>
                  {doc.highlights && doc.highlights.length > 0 && (
                    <div>
                      {doc.highlights.slice(0, 1).map((highlight, idx) => (
                        <Badge
                          key={idx}
                          bg="light"
                          text="dark"
                          className="me-1"
                          style={{
                            fontSize: '0.6rem',
                            padding: '0.15rem 0.3rem',
                          }}
                        >
                          {highlight}
                        </Badge>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Document Modal */}
      {showModal && selectedDocument && (
        <Modal show={showModal} onHide={handleCloseModal} size="xl" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedDocument.documentName}
              <Badge bg="secondary" className="ms-2">
                Page {selectedDocument.pageNumber}
                {selectedDocument.totalPages &&
                  ` of ${selectedDocument.totalPages}`}
              </Badge>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="text-center">
            {imageErrors[selectedDocument.id] ? (
              <div
                style={{
                  minHeight: '300px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  borderRadius: '4px',
                }}
              >
                <div>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                    📄
                  </div>
                  <div>Image unavailable</div>
                  <div style={{ fontSize: '12px', marginTop: '8px' }}>
                    The signed URL may have expired or the image is not
                    accessible.
                  </div>
                </div>
              </div>
            ) : (
              <>
                {imageLoadingStates[selectedDocument.id] && (
                  <div style={{ padding: '60px 0' }}>
                    <Spinner animation="border" />
                    <div style={{ marginTop: '16px', color: '#6c757d' }}>
                      Loading image...
                    </div>
                  </div>
                )}
                <img
                  src={selectedDocument.imageUrl}
                  alt={selectedDocument.documentName}
                  onLoadStart={() => handleImageLoadStart(selectedDocument.id)}
                  onLoad={() => handleImageLoad(selectedDocument.id)}
                  onError={() => handleImageError(selectedDocument.id)}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    minHeight: '600px',
                    objectFit: 'contain',
                    display: imageLoadingStates[selectedDocument.id]
                      ? 'none'
                      : 'block',
                    margin: '0 auto',
                  }}
                />
              </>
            )}

            {selectedDocument.summary && (
              <div className="mt-3 p-3 bg-light rounded">
                <strong>Summary:</strong> {selectedDocument.summary}
              </div>
            )}

            {selectedDocument.highlights &&
              selectedDocument.highlights.length > 0 && (
                <div className="mt-2">
                  <strong>Key Points:</strong>{' '}
                  {selectedDocument.highlights.map((highlight, idx) => (
                    <Badge key={idx} bg="light" text="dark" className="ms-1">
                      {highlight}
                    </Badge>
                  ))}
                </div>
              )}
          </Modal.Body>

          <Modal.Footer>
            {selectedDocument.pdfUrl && (
              <Button
                variant="outline-primary"
                onClick={() => window.open(selectedDocument.pdfUrl, '_blank')}
              >
                View Full PDF
              </Button>
            )}
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default DocumentGrid;
